/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Button — Premium Button with Shine Sweep & Spring Feedback
 * ============================================================================
 *
 * Features: gradient shine sweep on hover, spring-back press effect,
 * improved loading animation with pulse, glow pulse for CTA buttons,
 * icon micro-animation, prefers-reduced-motion support.
 *
 * ============================================================================
 */

import React, { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'link';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  glow?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      glow = false,
      disabled,
      className = '',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <>
        <style>{btnStyles}</style>
        <button
          ref={ref}
          disabled={isDisabled}
          className={`
            cx-btn
            cx-${variant}
            cx-sz-${size}
            ${fullWidth ? 'cx-full' : ''}
            ${glow ? 'cx-glow' : ''}
            ${loading ? 'cx-loading' : ''}
            ${className}
          `}
          {...props}
        >
          {/* Shine sweep overlay (primary & danger only) */}
          {(variant === 'primary' || variant === 'danger') && (
            <span className="cx-shine" aria-hidden="true" />
          )}

          {/* Spinner */}
          {loading && (
            <span className="cx-spinner">
              <Loader2 size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} />
            </span>
          )}

          {/* Left icon */}
          {leftIcon && !loading && (
            <span className="cx-icon cx-icon-left">{leftIcon}</span>
          )}

          {/* Content */}
          <span className="cx-content">
            {loading && loadingText ? loadingText : children}
          </span>

          {/* Right icon */}
          {rightIcon && !loading && (
            <span className="cx-icon cx-icon-right">{rightIcon}</span>
          )}
        </button>
      </>
    );
  }
);

Button.displayName = 'Button';

/* ─── Styles ─── */
const btnStyles = `
  /* ── Base ── */
  .cx-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: var(--font-heading);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    border-radius: 3px;
    border: none;
    cursor: pointer;
    overflow: hidden;
    white-space: nowrap;
    text-decoration: none;
    transition:
      transform 250ms cubic-bezier(0.16,1,0.3,1),
      box-shadow 250ms cubic-bezier(0.16,1,0.3,1),
      background 200ms ease,
      border-color 200ms ease,
      color 200ms ease;
  }

  .cx-btn:focus-visible {
    outline: 2px solid var(--color-brand-primary);
    outline-offset: 2px;
  }

  .cx-btn:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* ── Sizes ── */
  .cx-btn.cx-sz-sm {
    height: 36px;
    padding: 0 14px;
    font-size: 11px;
    border-radius: 3px;
  }
  .cx-btn.cx-sz-md {
    height: 44px;
    padding: 0 22px;
    font-size: 12px;
  }
  .cx-btn.cx-sz-lg {
    height: 48px;
    padding: 0 28px;
    font-size: 13px;
  }

  .cx-btn.cx-full { width: 100%; }

  /* ── Shine sweep (shared by primary & danger) ── */
  .cx-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(
      105deg,
      transparent 30%,
      rgba(255,255,255,0.12) 50%,
      transparent 70%
    );
    transition: none;
    pointer-events: none;
  }
  .cx-btn:hover:not(:disabled) .cx-shine {
    animation: cx-sweep 600ms cubic-bezier(0.16,1,0.3,1) forwards;
  }

  @keyframes cx-sweep {
    to { left: 120%; }
  }

  /* ── PRIMARY ── */
  .cx-btn.cx-primary {
    background: linear-gradient(
      135deg,
      var(--color-brand-primary) 0%,
      var(--color-brand-primary-hover) 100%
    );
    color: white;
    box-shadow:
      0 2px 8px rgba(0,0,0,0.2),
      inset 0 1px 0 rgba(255,255,255,0.08);
  }

  .cx-btn.cx-primary:hover:not(:disabled) {
    background: linear-gradient(
      135deg,
      var(--color-brand-primary-hover) 0%,
      var(--color-brand-primary-active) 100%
    );
    box-shadow:
      0 6px 20px var(--color-brand-glow, rgba(139,0,0,0.35)),
      inset 0 1px 0 rgba(255,255,255,0.1);
    transform: translateY(-1px);
  }

  .cx-btn.cx-primary:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  }

  /* Glow pulse for CTA */
  .cx-btn.cx-primary.cx-glow {
    animation: cx-glow-pulse 2.5s ease-in-out infinite;
  }
  .cx-btn.cx-primary.cx-glow:hover:not(:disabled) {
    animation: none;
  }

  @keyframes cx-glow-pulse {
    0%, 100% {
      box-shadow:
        0 2px 8px rgba(0,0,0,0.2),
        0 0 20px var(--color-brand-glow, rgba(139,0,0,0.25)),
        inset 0 1px 0 rgba(255,255,255,0.08);
    }
    50% {
      box-shadow:
        0 2px 8px rgba(0,0,0,0.2),
        0 0 35px var(--color-brand-glow, rgba(139,0,0,0.45)),
        inset 0 1px 0 rgba(255,255,255,0.08);
    }
  }

  /* ── SECONDARY ── */
  .cx-btn.cx-secondary {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
    border: 1.5px solid var(--color-border-default, rgba(255,255,255,0.08));
  }

  .cx-btn.cx-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover);
    border-color: var(--color-border-strong, rgba(255,255,255,0.18));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }

  .cx-btn.cx-secondary:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
  }

  /* ── GHOST ── */
  .cx-btn.cx-ghost {
    background: transparent;
    color: var(--color-text-secondary);
  }

  .cx-btn.cx-ghost:hover:not(:disabled) {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .cx-btn.cx-ghost:active:not(:disabled) {
    transform: scale(0.97);
  }

  /* ── DANGER ── */
  .cx-btn.cx-danger {
    background: linear-gradient(
      135deg,
      var(--color-error) 0%,
      var(--color-error-dark) 100%
    );
    color: white;
    box-shadow: 0 2px 8px rgba(220,38,38,0.15);
  }

  .cx-btn.cx-danger:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(220,38,38,0.35);
    transform: translateY(-1px);
  }

  .cx-btn.cx-danger:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
  }

  /* ── OUTLINE ── */
  .cx-btn.cx-outline {
    background: transparent;
    color: var(--color-brand-primary);
    border: 2px solid var(--color-brand-primary);
  }

  .cx-btn.cx-outline:hover:not(:disabled) {
    background: var(--color-brand-primary);
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px var(--color-brand-glow, rgba(139,0,0,0.2));
  }

  .cx-btn.cx-outline:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
  }

  /* ── LINK ── */
  .cx-btn.cx-link {
    background: transparent;
    color: var(--color-text-link);
    padding: 0;
    height: auto;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
    font-family: var(--font-body);
  }

  .cx-btn.cx-link:hover:not(:disabled) {
    color: var(--color-text-link-hover);
    text-decoration: underline;
  }

  /* ── Loading ── */
  .cx-btn.cx-loading {
    pointer-events: none;
  }

  .cx-spinner {
    display: flex;
    animation: cx-spin 0.9s linear infinite;
  }

  @keyframes cx-spin {
    to { transform: rotate(360deg); }
  }

  /* ── Icons ── */
  .cx-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 200ms cubic-bezier(0.16,1,0.3,1);
  }

  .cx-btn:hover:not(:disabled) .cx-icon-left {
    transform: translateX(-1px);
  }
  .cx-btn:hover:not(:disabled) .cx-icon-right {
    transform: translateX(1px);
  }

  .cx-content {
    display: flex;
    align-items: center;
  }

  /* ── Reduced motion ── */
  @media (prefers-reduced-motion: reduce) {
    .cx-btn,
    .cx-shine,
    .cx-spinner,
    .cx-icon {
      animation: none !important;
      transition-duration: 0ms !important;
    }
    .cx-btn:hover:not(:disabled) {
      transform: none !important;
    }
  }
`;

export default Button;
